"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { authApi, type Session } from "@/lib/api";

type AuthContextValue = {
  session: Session | null;
  login: (
    email: string,
    password: string,
    remember?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  initialSession,
  children,
}: {
  initialSession: Session | null;
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<Session | null>(initialSession);

  const login = useCallback(
    async (email: string, password: string, remember = false) => {
      await authApi.login(email, password, remember);
      // /me devuelve además el workspace activo (lo inyecta LoadWorkspace).
      setSession(await authApi.me());
    },
    [],
  );

  const logout = useCallback(async () => {
    await authApi.logout();
    setSession(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      setSession(await authApi.me());
    } catch {
      setSession(null);
    }
  }, []);

  const value = useMemo(
    () => ({ session, login, logout, refresh }),
    [session, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  return ctx;
}
